import React, { useEffect, useMemo, useState } from "react";
import api from "services/api";
import { __ } from "services/Translator";

// Deliberately the existing /get-faqs endpoint, not a new support-side search.
// The knowledge base players already read lives there and is already curated
// per language, so pointing the messenger at a second store would mean two
// places to keep an answer correct.
//
// Shape: [{ category, faqs: [{ title, description }] }]. The filter runs on the
// client because the whole set is one small payload and a keystroke should not
// be a request.
function flatten(categories) {
  const out = [];
  (categories || []).forEach((category) => {
    (category.faqs || []).forEach((faq) => {
      out.push({
        category: category.category,
        title: faq.title,
        description: faq.description,
      });
    });
  });
  return out;
}

export default function KbSearchView({ onStartConversation }) {
  const [entries, setEntries] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const response = await api.get("/get-faqs");
      if (!alive) {
        return;
      }
      setLoading(false);
      if (response.status === 200 && Array.isArray(response.data)) {
        setEntries(flatten(response.data));
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const results = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return entries;
    }
    return entries.filter(
      (entry) =>
        (entry.title || "").toLowerCase().indexOf(needle) !== -1 ||
        (entry.description || "").toLowerCase().indexOf(needle) !== -1
    );
  }, [entries, query]);

  return (
    <div className="supKb">
      <div className="supField">
        <label className="supLabel" htmlFor="supKbSearch">
          {__("Search help articles")}
        </label>
        <input
          id="supKbSearch"
          type="search"
          className="supInput"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={__("What do you need help with?")}
        />
      </div>

      {loading && (
        <div className="supState" role="status">
          {__("Loading")}
        </div>
      )}

      {!loading && !results.length && (
        <p className="supEmpty">
          {query ? __("No articles matched that search.") : __("No help articles yet.")}
        </p>
      )}

      <ul className="supKbList" aria-live="polite">
        {results.map((entry, index) => (
          <li key={entry.category + "-" + index}>
            <button
              type="button"
              className="supKbItem"
              aria-expanded={openIndex === index}
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            >
              <span className="supKbTitle">{entry.title}</span>
              <span className="supKbCategory">{entry.category}</span>
            </button>
            {openIndex === index && <p className="supKbBody">{entry.description}</p>}
          </li>
        ))}
      </ul>

      {onStartConversation && (
        <button type="button" className="supBtn supBtnGhost supKbAsk" onClick={onStartConversation}>
          {__("Still need help? Message us")}
        </button>
      )}
    </div>
  );
}
